import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismLoadingBar,
  PrismPagination,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import HotelAndMarketFormFieldsHotelOrMarketSearch from 'lumen/components/hotel-and-market/form-fields/hotel-or-market-search';
import HotelAndMarketFormFieldsSingleUserSearch from 'lumen/components/hotel-and-market/form-fields/single-user-search';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsReportsTablesTableReports from 'lumen/components/settings/reports/tables/table-reports';
import type MarketSettingsReportsController from 'lumen/controllers/market/settings/reports';

interface MarketSettingsReportsSignature {
  Args: {
    controller: MarketSettingsReportsController;
    model: unknown;
  };
}

<template>
  {{#if @controller.model.isLoading}}
    <PrismLoadingBar @isFixed={{true}} @positionTop={{60}} />
  {{/if}}

  <SettingsPageTemplate
    @description={{t "settings.reports.description"}}
    @isFullHeight={{true}}
    @title={{t "settings.section.scheduled_reports"}}
  >
    <div class="flex h-full flex-col">
      <div class="settings-reports-container flex flex-1 flex-col">
        <div class="settings-reports-table-extra-header flex justify-between">
          <span>
            {{#unless @controller.model.isLoading}}
              {{t
                "settings.reports.reports"
                count=@controller.model.totalResults
              }}
            {{/unless}}
          </span>

          <div
            class="settings-reports-table-filter-wrapper flex flex-row items-center justify-center gap-200"
          >
            <HotelAndMarketFormFieldsSingleUserSearch
              {{! @glint-expect-error: Incorrect type }}
              @defaultOptionLabel={{t "reports.all_recipients_filter"}}
              @selectItem={{@controller.selectUser}}
              @selectedOption={{@controller.selectedRecipient}}
            />

            <HotelAndMarketFormFieldsHotelOrMarketSearch
              @selectItem={{@controller.selectHotelOrMarket}}
              @selectedOption={{@controller.selectedHotelOrMarket}}
            />

            {{#if @controller.access.isStaffUser}}
              <HotelAndMarketFormFieldsSingleUserSearch
                {{! @glint-expect-error: Incorrect type }}
                @defaultOptionLabel={{t
                  "reports.impersonate_user_filter_default"
                }}
                @selectItem={{@controller.selectUserToImpersonate}}
                @selectedOption={{@controller.selectedImpersonator}}
              />
            {{/if}}

            <PrismSelectInput
              @horizontalPosition="right"
              @onChange={{@controller.filterType}}
              @options={{@controller.reportTypes}}
              @value={{@controller.selectedReportType}}
            />

            <PrismSelectInput
              @horizontalPosition="right"
              @onChange={{@controller.filterReports}}
              @options={{@controller.reportCategories}}
              @value={{@controller.selectedReportCategory}}
            />
          </div>
        </div>

        <SettingsReportsTablesTableReports
          {{! @glint-expect-error: Incorrect type }}
          @arrReportsSelectedForBulkDeletion={{@controller.arrReportsSelectedForBulkDeletion}}
          @doRefresh={{@controller.doRefresh}}
          @isLoading={{@controller.model.isLoading}}
          @model={{@controller.convertedModel}}
          @name="settings-reports-table"
          @nrOfDeletableReports={{@controller.deletableReports.length}}
          @rowSelection={{@controller.rowSelection}}
          @selectAllReportsForBulkDeletion={{@controller.selectAllReportsForBulkDeletion}}
          @setRowSelection={{@controller.setRowSelection}}
          @sortBy={{@controller.sortBy}}
          @sortDirection={{@controller.sortDirection}}
          @sortResults={{@controller.sortResults}}
          @toggleReportForBulkDeletion={{@controller.toggleReportForBulkDeletion}}
        />

        {{#if @controller.arrReportsSelectedForBulkDeletion.length}}
          <div class="flex items-center gap-200 p-200">
            <PrismButton
              @buttonType="critical"
              @label={{t "general.delete"}}
              @onClick={{@controller.bulkDeleteSelectedReports}}
            />
            <p class="text-300--regular">
              {{t
                "settings.reports.reports_selected"
                count=@controller.arrReportsSelectedForBulkDeletion.length
              }}
            </p>
          </div>
        {{/if}}
      </div>

      <div class="pagination-row-container">
        <PrismPagination
          @lastPage={{@controller.model.totalPages}}
          @page={{@controller.page}}
          @setPage={{@controller.setPage}}
        />
      </div>
    </div>
  </SettingsPageTemplate>
</template> satisfies TOC<MarketSettingsReportsSignature>;
